import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { formatCurrency, formatShortDateWithoutTimezone, getAccountName, isValidCpf } from '@ibk/shared-utils';
import { BankName, DocumentTypeDescription, PixKeyTypeDescription } from '../../enums';
import { toPixKeyTypeDescription } from '../../utils/pixKeyUtils';
function getDocumentType(documentNumber, transactionDocumentType) {
    if (transactionDocumentType) {
        return transactionDocumentType;
    }
    if (documentNumber && isValidCpf(documentNumber)) {
        return DocumentTypeDescription.CPF;
    }
    return DocumentTypeDescription.CNPJ;
}
function getPixKeyType(transactionKeyType, transferKeyType, transferKeyValue) {
    if (transactionKeyType) {
        return transactionKeyType;
    }
    if (transferKeyType && transferKeyValue) {
        return toPixKeyTypeDescription(transferKeyType, transferKeyValue) ?? PixKeyTypeDescription.EMAIL;
    }
    return PixKeyTypeDescription.EMAIL;
}
function buildOriginData(accountInfo, t) {
    const document = accountInfo?.documentNumber ?? '';
    return {
        name: getAccountName(accountInfo) || t('not-available'),
        document,
        documentType: getDocumentType(document),
        bank: BankName.CELCOIN,
    };
}
function buildPixDestinationData(transaction, transferData) {
    const destinationName = transaction?.details?.counterparty?.name ??
        transferData?.pixKeyDetails?.name ??
        transferData?.pixAgencyAccount?.name;
    const destinationDocument = transaction?.details?.counterparty?.document ??
        transferData?.pixKeyDetails?.taxIdentifier ??
        transferData?.pixAgencyAccount?.document;
    const destinationDocumentType = getDocumentType(destinationDocument, transaction?.details?.counterparty?.documentType);
    const destinationBank = transaction?.details?.counterparty?.bank?.name ??
        transferData?.pixKeyDetails?.bank ??
        transferData?.pixAgencyAccount?.bankName;
    return {
        name: destinationName,
        document: destinationDocument,
        documentType: destinationDocumentType,
        bank: destinationBank,
        key: transaction?.details?.key ?? transferData?.keyValue,
    };
}
function buildScheduledDestinationData(transferData, t) {
    const destinationDocument = transferData?.pixKeyDetails?.taxIdentifier ?? '';
    return {
        name: transferData?.pixKeyDetails?.name ?? t?.('not-available') ?? '',
        document: destinationDocument,
        documentType: getDocumentType(destinationDocument),
        bank: transferData?.pixKeyDetails?.bank ?? t?.('not-available') ?? '',
        key: transferData?.keyValue ?? '',
    };
}
function buildPixSuccessData(pixData, transferData, accountInfo, t) {
    const { transaction } = pixData;
    const origin = buildOriginData(accountInfo, t);
    const destination = buildPixDestinationData(transaction, transferData);
    return {
        id: pixData.id,
        amount: transaction?.amount ?? pixData.amount,
        date: transaction?.date ?? pixData.date,
        endToEndId: transaction?.endToEndId ?? '',
        keyType: getPixKeyType(transaction?.details?.keyType, transferData.keyType, transferData.keyValue),
        key: transaction?.details?.key ?? transferData.keyValue,
        description: transaction?.details?.description ?? '',
        origin,
        destination,
    };
}
function buildScheduledSuccessData(scheduledData, transferData, accountInfo, t) {
    const origin = buildOriginData(accountInfo, t);
    const destination = buildScheduledDestinationData(transferData, t);
    return {
        id: scheduledData.instructionScheduleId,
        amount: scheduledData.amount,
        date: scheduledData.scheduledDate,
        endToEndId: '',
        keyType: toPixKeyTypeDescription(transferData.keyType, transferData.keyValue) ?? PixKeyTypeDescription.EMAIL,
        key: transferData.keyValue,
        description: '',
        origin,
        destination,
    };
}
function buildDefaultSuccessData(t) {
    return {
        id: '',
        amount: 0,
        date: new Date().toISOString(),
        endToEndId: '',
        keyType: PixKeyTypeDescription.EMAIL,
        key: '',
        description: '',
        origin: {
            name: t('not-available'),
            document: '',
            documentType: DocumentTypeDescription.CPF,
            bank: BankName.CELCOIN,
        },
        destination: {
            name: t('not-available'),
            document: '',
            documentType: DocumentTypeDescription.CPF,
            bank: t('not-available'),
            key: '',
        },
    };
}
function checkIsPendingTransaction(isWhiteListed, pixData, scheduledData) {
    if (pixData && isWhiteListed) {
        return !pixData.transaction?.transactionId;
    }
    if (scheduledData && isWhiteListed) {
        return !scheduledData?.isApproved;
    }
    return false;
}
function getSuccessTitle(isPendingTransaction, t, scheduledData) {
    if (isPendingTransaction) {
        if (scheduledData) {
            return t('pending-schedule');
        }
        return t('pending-transfer');
    }
    return t('success-title');
}
function getSuccessMessage(isPendingTransaction, t, scheduledData) {
    if (isPendingTransaction) {
        if (scheduledData) {
            return t('your-schedule-is-pending-approval');
        }
        return t('your-transfer-is-pending-approval');
    }
    return scheduledData ? t('scheduled-transfer-success-message') : t('transfer-success-message');
}
function getBackButtonLabel(isPendingTransaction, t) {
    if (isPendingTransaction) {
        return t('back-to-area-pix');
    }
    return t('show-receipt');
}
export function usePixTransferSuccess({ pixData, scheduledData, transferData, }) {
    const { t } = useTranslation();
    const { maskDocumentHidden } = useCnpjAlphanumeric();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const navigate = useNavigate();
    const accountInfo = getSelectedAccountInfo();
    const [showReceipt, setShowReceipt] = useState(false);
    const isPendingTransaction = useMemo(() => {
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
        return checkIsPendingTransaction(isWhiteListed, pixData, scheduledData);
    }, [pixData, scheduledData]);
    const successData = useMemo(() => {
        if (pixData && transferData) {
            return buildPixSuccessData(pixData, transferData, accountInfo, t);
        }
        if (scheduledData && transferData) {
            return buildScheduledSuccessData(scheduledData, transferData, accountInfo, t);
        }
        return buildDefaultSuccessData(t);
    }, [pixData, scheduledData, transferData, accountInfo, t]);
    const formattedAmount = useMemo(() => {
        return formatCurrency(successData.amount);
    }, [successData.amount]);
    const formattedDate = useMemo(() => {
        return formatShortDateWithoutTimezone(successData.date);
    }, [successData.date]);
    const successTitle = useMemo(() => getSuccessTitle(isPendingTransaction, t, scheduledData), [isPendingTransaction, t, scheduledData]);
    const successMessage = useMemo(() => getSuccessMessage(isPendingTransaction, t, scheduledData), [isPendingTransaction, t, scheduledData]);
    const backButtonLabel = useMemo(() => getBackButtonLabel(isPendingTransaction, t), [isPendingTransaction, t]);
    const handleViewReceipt = () => {
        setShowReceipt(true);
    };
    const handleCloseReceipt = () => {
        setShowReceipt(false);
    };
    const handleNewTransfer = () => {
        navigate('/conta-pj/pix/transferir');
    };
    const handleGoToPix = () => {
        navigate('/conta-pj/pix');
    };
    const handleBackButton = () => {
        if (isPendingTransaction) {
            handleGoToPix();
        }
        else {
            handleViewReceipt();
        }
    };
    const formatDocument = useCallback((document) => {
        if (!document)
            return t('not-available');
        return maskDocumentHidden(document) ?? t('not-available');
    }, [t, maskDocumentHidden]);
    const getDocumentLabel = useCallback((document) => {
        if (!document)
            return t('cpf-cnpj').toUpperCase();
        return isValidCpf(document) ? t('cpf').toUpperCase() : t('cnpj').toUpperCase();
    }, [t]);
    return {
        successData,
        formattedAmount,
        formattedDate,
        successTitle,
        successMessage,
        showReceipt,
        isPendingTransaction,
        handleViewReceipt,
        handleCloseReceipt,
        handleNewTransfer,
        formatDocument,
        getDocumentLabel,
        handleBackButton,
        backButtonLabel,
    };
}
